import { Auth } from "aws-amplify";
import { getUserId } from "./userUtils";
import { endpoints } from "../config/endpoints";

export const checkAwsCredentials = async () => {
  try {
    const credentials = await Auth.currentCredentials();
    console.log("Identity Id:", credentials.identityId);
    const essentialCredentials = Auth.essentialCredentials(credentials);
    console.log("AWS Credentials:", essentialCredentials);
    return essentialCredentials;
  } catch (error) {
    console.error("Error getting AWS credentials:", error);
    throw error;
  }
};

// New file to manage WebSocket connection
export class WebSocketManager {
  constructor(userId) {
    this.socket = null;
    this.userId = userId;
    this.messageHandlers = new Set();
    this.isConnected = false;
    this.pendingRetries = new Map(); // Track messages being retried
    this.emptyResponseCount = 0; // Track consecutive empty responses
    console.log(`WebSocketManager initialized with userId: ${userId}`);
  }

  async connect() {
    try {
      // If we're already connecting, don't try to connect again
      if (this.isConnecting) {
        console.log("Already attempting to connect to WebSocket");
        return;
      }

      this.isConnecting = true;

      // Get the current session token from Amplify Auth
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      if (!idToken) {
        console.error("No authentication token available");
        this.isConnecting = false;
        throw new Error("No authentication token available");
      }

      // Add the token as a query parameter using the configured endpoint
      const wsUrl = `${endpoints.websocket}?token=${idToken}`;
      console.log("Connecting to WebSocket URL with token");

      // Close existing socket if it exists
      if (this.socket) {
        console.log("Closing existing WebSocket connection");
        this.socket.close();
      }

      // Store connection timestamp
      this.connectionTimestamp = Date.now();

      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log("Connected to WebSocket");
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection

        // Send a ping every 30 seconds to keep the connection alive
        // AWS API Gateway WebSocket connections timeout after ~10 minutes of inactivity
        // but some network configurations may close idle connections sooner
        if (this.pingTimer) {
          clearInterval(this.pingTimer);
        }

        this.pingTimer = setInterval(() => {
          if (
            this.isConnected &&
            this.socket &&
            this.socket.readyState === WebSocket.OPEN
          ) {
            console.log("Sending ping to keep WebSocket connection alive");
            try {
              // Send a ping message
              this.socket.send(JSON.stringify({ action: "ping" }));
            } catch (error) {
              console.error("Error sending ping:", error);
              // If ping fails, try to reconnect
              this.connect().catch((error) => {
                console.error("Failed to reconnect after ping error:", error);
              });
            }
          }
        }, 30 * 1000); // Ping every 30 seconds

        // Set a timer to refresh the connection before token expires (typically 1 hour)
        // This helps prevent "No token provided" errors
        if (this.refreshTimer) {
          clearTimeout(this.refreshTimer);
        }

        this.refreshTimer = setTimeout(
          () => {
            console.log(
              "Refreshing WebSocket connection to prevent token expiration"
            );
            this.connect().catch((error) => {
              console.error("Failed to refresh WebSocket connection:", error);
            });
          },
          50 * 60 * 1000
        ); // Refresh after 50 minutes (tokens typically expire after 1 hour)
      };

      this.socket.onmessage = (event) => {
        try {
          console.log("Raw WebSocket message received:", event.data);

          // Check if the message is empty or invalid
          if (!event.data) {
            console.error("Empty WebSocket message received");
            return;
          }

          const response = JSON.parse(event.data);
          console.log("WebSocket received parsed message:", response);

          // Only process message for display if it's not an enqueue action
          if (response.action !== "enqueue") {
            // Check if we have a valid response
            // First check for receivedmessage action which is the standard format from middleware
            if (response.action === "receivedmessage" && response.data) {
              console.log("Received standard middleware response format");
              // Extract the actual response content from the middleware wrapper
              const responseContent = response.data.response;

              // Extract thread_id from the response if available
              // This is crucial for routing the message to the correct chat thread
              const threadId =
                response.data.thread_id || this.lastMessage?.thread_id;

              if (threadId) {
                console.log(`Response associated with thread_id: ${threadId}`);
              } else {
                console.warn("No thread_id found in response or lastMessage");
              }

              // Only show error for empty content if we're not in a retry situation
              // and we've received multiple empty responses
              const jobId = response.job_id || this.lastMessage?.job_id;
              const isBeingRetried = jobId && this.pendingRetries.has(jobId);

              // Check for truly empty content (null, undefined, or empty string)
              const isEmptyContent = !responseContent || responseContent === "";

              if (isEmptyContent) {
                this.emptyResponseCount++;
                console.warn(
                  `Empty response count: ${this.emptyResponseCount}`
                );
              } else {
                this.emptyResponseCount = 0;
              }

              const formattedResponse = {
                type: "ai_response",
                answer: isEmptyContent
                  ? isBeingRetried || this.emptyResponseCount < 2
                    ? "" // Use empty string instead of null
                    : "I'm sorry, I couldn't process that message. Please try again."
                  : responseContent,
                thread_id: threadId, // Include thread_id in the response
              };

              // Don't send empty responses to UI if we're retrying
              if (
                (!formattedResponse.answer ||
                  formattedResponse.answer === "") &&
                isBeingRetried
              ) {
                console.log(
                  "Skipping empty response for message being retried"
                );
                return;
              }

              console.log("Formatted response for UI:", formattedResponse);
              this.messageHandlers.forEach((handler) =>
                handler(formattedResponse)
              );
              return;
            }

            // Fallback to checking other response formats
            const responseContent =
              response.data?.response ||
              response.message ||
              response.answer ||
              response.body;

            // Check for error messages
            const errorMessage = response.error || response.data?.error;
            if (errorMessage) {
              console.error(
                "Error message received from WebSocket:",
                errorMessage
              );

              // Check for 409 conflict error
              if (
                errorMessage.includes("409") ||
                errorMessage.toLowerCase().includes("conflict")
              ) {
                console.warn(
                  "Detected 409 conflict error - attempting to retry after delay"
                );

                // Retry the message with a delay
                const retrySuccessful = this.retryLastMessage(5000); // 5 second delay

                if (retrySuccessful) {
                  console.log("Message retry scheduled");
                  // Add to pending retries with the job ID
                  if (this.lastMessage && this.lastMessage.job_id) {
                    this.pendingRetries.set(
                      this.lastMessage.job_id,
                      Date.now()
                    );
                    console.log(
                      `Added job_id ${this.lastMessage.job_id} to pending retries`
                    );
                  }
                  return; // Don't show error to user if we're retrying
                }
              }

              const formattedResponse = {
                type: "ai_response",
                answer: `Error: ${errorMessage}`,
              };

              this.messageHandlers.forEach((handler) =>
                handler(formattedResponse)
              );
              return;
            }

            // Check if this message is being retried
            const jobId = response.job_id || this.lastMessage?.job_id;
            const isBeingRetried = jobId && this.pendingRetries.has(jobId);

            // Check for truly empty content (null, undefined, or empty string)
            const isEmptyContent = !responseContent || responseContent === "";

            if (isEmptyContent) {
              this.emptyResponseCount++;
              console.warn(`Empty response count: ${this.emptyResponseCount}`);
            } else {
              this.emptyResponseCount = 0;
            }

            const formattedResponse = {
              type: "ai_response",
              answer: isEmptyContent
                ? isBeingRetried || this.emptyResponseCount < 2
                  ? "" // Use empty string instead of null
                  : "I'm sorry, I couldn't process that message. Please try again."
                : responseContent,
              thread_id: this.lastMessage?.thread_id, // Include thread_id from last message
            };

            // Don't send empty responses to UI if we're retrying or if it's the first empty response
            if (
              (!formattedResponse.answer || formattedResponse.answer === "") &&
              (isBeingRetried || this.emptyResponseCount < 2)
            ) {
              console.log(
                "Skipping empty response - either being retried or first empty response"
              );
              return;
            }

            // Log if we received an empty message
            if (!responseContent) {
              console.warn(
                "Received empty message content from WebSocket:",
                response
              );
              console.warn(
                "Response structure:",
                JSON.stringify(response, null, 2)
              );
            }

            console.log(
              "Sending formatted response to UI handlers:",
              formattedResponse
            );
            this.messageHandlers.forEach((handler) =>
              handler(formattedResponse)
            );
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
          console.error("Raw message that caused error:", event.data);

          // Try to send a formatted error to the UI
          try {
            const formattedResponse = {
              type: "ai_response",
              answer:
                "Error: Failed to parse server response. Please try again.",
            };

            this.messageHandlers.forEach((handler) =>
              handler(formattedResponse)
            );
          } catch (innerError) {
            console.error("Failed to send error message to UI:", innerError);
          }
        }
      };

      this.socket.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      this.socket.onclose = (event) => {
        console.log(
          `WebSocket disconnected with code: ${event.code}, reason: ${event.reason}`
        );
        this.isConnected = false;

        // Clear any existing refresh timer
        if (this.refreshTimer) {
          clearTimeout(this.refreshTimer);
          this.refreshTimer = null;
        }

        // Don't attempt to reconnect if this was a normal closure
        if (event.code !== 1000) {
          // Attempt to reconnect with exponential backoff
          const backoffTime = this.reconnectAttempts
            ? Math.min(3000 * Math.pow(1.5, this.reconnectAttempts), 30000)
            : 3000;
          this.reconnectAttempts = (this.reconnectAttempts || 0) + 1;

          console.log(
            `Attempting to reconnect in ${backoffTime / 1000} seconds... (Attempt ${this.reconnectAttempts})`
          );

          this.reconnectTimer = setTimeout(() => {
            console.log("Reconnecting to WebSocket...");
            this.connect().catch((error) => {
              console.error("Failed to reconnect to WebSocket:", error);
            });
          }, backoffTime);
        } else {
          console.log("WebSocket closed normally, not attempting to reconnect");
        }
      };
    } catch (error) {
      console.error("Error establishing WebSocket connection:", error);
    }
  }

  addMessageHandler(handler) {
    this.messageHandlers.add(handler);
  }

  removeMessageHandler(handler) {
    this.messageHandlers.delete(handler);
  }

  async sendMessage(message) {
    // Check if we're connected, if not, try to reconnect
    if (!this.socket || !this.isConnected) {
      console.log(
        "WebSocket not connected, attempting to reconnect before sending message"
      );
      try {
        await this.connect();
      } catch (error) {
        console.error("Failed to reconnect WebSocket:", error);
        return;
      }
    }

    // Validate required fields
    if (!message.thread_id) {
      console.error("Error: thread_id is required for sending messages");
      return;
    }

    // Generate a unique job ID for this message
    const jobId = this.generateJobId();

    const formattedMessage = {
      action: "sendmessage",
      message: message.human_input, // Changed from human_input to message to match middleware expectations
      bot_id: message.bot_id,
      user_id: this.userId,
      file_url: message.file_url || null,
      file_type: message.file_type || null,
      file_recorded_audio: message.file_recorded_audio || null,
      initial_message_type: message.initial_message_type || null,
      thread_id: message.thread_id,
      job_id: jobId,
    };

    // Store the last message for potential retries and thread tracking
    this.lastMessage = {
      ...formattedMessage,
      originalData: message, // Keep the original data for reference
      thread_id: message.thread_id, // Explicitly store thread_id for response matching
    };

    console.log(
      `Sending WebSocket message with job_id: ${jobId}, thread_id: ${message.thread_id}`
    );

    try {
      this.socket.send(JSON.stringify(formattedMessage));
    } catch (error) {
      console.error("Error sending message through WebSocket:", error);

      // If there's an error, try to reconnect and send again
      try {
        console.log("Attempting to reconnect and resend message");
        await this.connect();
        this.socket.send(JSON.stringify(formattedMessage));
      } catch (retryError) {
        console.error(
          "Failed to resend message after reconnection:",
          retryError
        );
      }
    }
  }

  // Generate a unique job ID
  generateJobId() {
    return (
      "job_" +
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  // Method to retry sending the last message with a new job ID
  retryLastMessage(delay = 5000) {
    if (!this.lastMessage) {
      console.error("No previous message to retry");
      return false;
    }

    if (!this.socket || !this.isConnected) {
      console.error("WebSocket is not connected");
      return false;
    }

    // Wait before retrying to avoid conflicts
    console.log(`Waiting ${delay}ms before retrying message...`);

    // Clear any old pending retries that are more than 30 seconds old
    this.cleanupPendingRetries();

    setTimeout(() => {
      // Create a new message with the same data but a new job ID
      const newJobId = this.generateJobId();
      const retryMessage = {
        ...this.lastMessage,
        job_id: newJobId,
      };

      // Store the new job ID in pending retries
      this.pendingRetries.set(newJobId, Date.now());

      console.log(
        `Retrying message with new job_id: ${newJobId}`,
        retryMessage
      );
      this.socket.send(JSON.stringify(retryMessage));

      // Reset empty response counter when retrying
      this.emptyResponseCount = 0;
    }, delay);

    return true;
  }

  // Clean up pending retries that are older than 30 seconds
  cleanupPendingRetries() {
    const now = Date.now();
    for (const [jobId, timestamp] of this.pendingRetries.entries()) {
      if (now - timestamp > 30000) {
        // 30 seconds
        console.log(`Removing stale retry job_id: ${jobId}`);
        this.pendingRetries.delete(jobId);
      }
    }
  }

  disconnect() {
    // Clear any timers
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }

    // Reset reconnect attempts and connection state
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.pendingRetries.clear();
    this.emptyResponseCount = 0;

    // Close the socket
    if (this.socket) {
      this.socket.close(1000, "Normal closure");
      this.socket = null;
      this.isConnected = false;
    }
  }
}
